/**
 * useTextSectionTime
 * ==================
 *
 * Study time per concept in the lesson text: the text is written section by
 * section (`## ` headings), and each section's concepts are marked in it as
 * `<concept id="...">` spans. As the student scrolls, the section whose
 * heading has passed the top third of the reading area is the one being read;
 * its concepts go to `useConceptTime`, which sums the time and credits it to
 * Mindspace.
 */

import { useEffect, type RefObject } from 'react';
import { useConceptTime } from '@/hooks/useConceptTime';

const CONCEPT_ID = /<concept\s+id="([^"]+)"/g;

/** Concept ids per `## ` section of the markdown, in order (the text before
 *  the first heading is section -1 and is not returned). */
export function sectionConceptIds(markdown: string): string[][] {
  const parts = markdown.split(/^## /m).slice(1);
  return parts.map(part => [
    ...new Set([...part.matchAll(CONCEPT_ID)].map(m => m[1])),
  ]);
}

export function useTextSectionTime(
  lessonId: string | undefined,
  markdown: string,
  scrollRef: RefObject<HTMLElement | null>,
  contentRef: RefObject<HTMLElement | null>
) {
  const setVisible = useConceptTime(lessonId);

  useEffect(() => {
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (!scroller || !content || !markdown) return;
    const sections = sectionConceptIds(markdown);
    if (sections.length === 0) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const box = scroller.getBoundingClientRect();
      const line = box.top + box.height / 3;
      const headings = [...content.querySelectorAll('h2')];
      let current = -1;
      headings.forEach((h, i) => {
        if (h.getBoundingClientRect().top <= line) current = i;
      });
      setVisible(current >= 0 ? (sections[current] ?? []) : []);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
      setVisible([]);
    };
  }, [markdown, scrollRef, contentRef, setVisible]);
}
